import { type JSX,useCallback, useEffect, useState } from 'react';

import { Box, Stack, TextField } from '@mui/material';
import { useAppDispatch,useAppSelector } from 'app/hooks';
import { useDrawerContext } from 'common/components/DrawerContext/drawerContext';
import { EditDrawer } from 'common/components/EditDrawer';
import {
    selectCurrentEntity,
    setCurrentEntity,
    setNameInConflict,
    setSaveButtonEnabled
} from 'common/components/EditDrawer/editDrawerSlice';
import { ReadOnlyTextField } from 'common/components/sharedCustomComponents/ReadOnlyTextField/ReadOnlyTextField';
import { UserProfilesDropDown } from 'common/components/sharedCustomComponents/userProfiles/UserProfilesDropDown';
import { fetchIdentitiesById } from 'common/services/api/Identities/Identities.thunk';
import { APIUserPatchParams } from 'common/services/api/Users/Users.api';
import { patchUser } from 'common/services/api/Users/Users.thunk';
import { UserDetails,UserProfileOemMapping } from 'common/types/Entities';
import { DrawerEntitiesNames, Operations } from 'common/types/TypeDefs';
import { DrawerIDs } from 'common/utils/DrawerIDs';
import { editDrawerTextFieldStyles } from 'common/utils/StyleHelpers';
import { selectUserDetails, setUserDetails } from 'features/users/usersSlice';

interface IdentitiesDrawerProperties {
    oemId: string;
}

function useUserProfileIdentitiesDrawer(
    entityName: string,
    drawerProps: IdentitiesDrawerProperties
) {
    const dispatch = useAppDispatch();
    const [userProfile, setUserProfile] = useState<UserProfileOemMapping>();

    const drawerContext = useDrawerContext<
        never,
        IdentitiesDrawerProperties,
        APIUserPatchParams,
        UserDetails,
        never,
        never
    >(entityName, drawerProps);

    useEffect(() => {
        if (drawerContext.currentId === DrawerIDs.UserProfileIdentities) {
            setUserProfile(undefined);
            dispatch(setNameInConflict(false));
        }
    }, [drawerContext.isOpenDrawer, drawerContext.currentId, dispatch]);

    useEffect(() => {
        handleSaveBtnEnabled();
        dispatch(setNameInConflict(false));
    }, [userProfile, dispatch]);

    const handleUserProfileChange = useCallback((_e: unknown, profile?: UserProfileOemMapping | null) => {
        setUserProfile(profile ?? undefined);
    }, []);

    const handleUserProfileTextareas = useCallback((field: keyof UserProfileOemMapping) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setUserProfile(prev => prev ? { ...prev, [field]: e.target.value } : prev);
    }, []);

    const handleSaveBtnEnabled = useCallback(() => {
        const existsInOem = !!userProfile?.oemId;
        let enabled = false;
        if (!existsInOem) {
            const hasSubject = !!userProfile?.subject;
            if (hasSubject) {
                enabled = true;
            }
            else {
                const hasRequiredFields =
                    !!userProfile?.firstName && !!userProfile?.lastName && !!userProfile?.email && !!userProfile?.companyKey;
                enabled = hasRequiredFields;
            }
        }
        dispatch(setSaveButtonEnabled(enabled));
    }, [dispatch, userProfile?.oemId, userProfile?.subject, userProfile?.firstName, userProfile?.lastName, userProfile?.email, userProfile?.companyKey]);

    const createSuccessHandler = useCallback((postResult: UserDetails) => {
        drawerContext.createSuccessHandler(postResult);
        dispatch(setUserDetails(postResult));
        dispatch(setCurrentEntity());
        dispatch(fetchIdentitiesById({ userId: drawerProps.oemId })).catch(console.error);
    }, [drawerContext, dispatch, drawerProps.oemId]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const createFailureHandler = useCallback((error: any) => {
        drawerContext.createFailureHandler(error);
    }, [drawerContext]);

    const getPostOptions = useCallback((): APIUserPatchParams | null => {
        if (!userProfile) {
            return null;
        }
        return {
            userId: drawerProps.oemId,
            identity: {
                issuer: userProfile?.authority || '',
                subject: userProfile?.subject || '',
                profile: {
                    firstName: userProfile?.firstName || '',
                    lastName: userProfile?.lastName || '',
                    email: userProfile?.email || '',
                    companyKey: userProfile?.companyKey ?? '',
                }
            },
        };
    }, [drawerProps.oemId, userProfile]);

    const postCallback = useCallback(() => {
        drawerContext.postCallback(patchUser, getPostOptions, createSuccessHandler, createFailureHandler);
    }, [drawerContext, getPostOptions, createSuccessHandler, createFailureHandler]);

    return {
        ...drawerContext,
        userProfile,
        handleUserProfileChange,
        handleUserProfileTextareas,
        postCallback
    };
}

function CreateContent({
    entityName,
    processing,
    nameInConflict,
    userProfile,
    handleUserProfileChange,
    handleUserProfileTextareas,
    postCallback
}: Readonly<{
    entityName: string;
    processing: boolean;
    nameInConflict: boolean;
    userProfile?: UserProfileOemMapping;
    handleUserProfileChange: (_e: unknown, profile?: UserProfileOemMapping | null) => void;
    handleUserProfileTextareas: (field: keyof UserProfileOemMapping) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    postCallback: () => void;
}>): JSX.Element {
    return (
        <EditDrawer
            title={entityName}
            saveButtonFunction={postCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <UserProfilesDropDown
                    value={userProfile ?? null}
                    onChange={handleUserProfileChange}
                    placeholder={'Search by User\'s company email address'}
                    allowCreateDjUser={true}
                />
                {userProfile &&
                    <>
                        <TextField
                            sx={editDrawerTextFieldStyles()}
                            size="small"
                            label="First Name"
                            variant="filled"
                            value={userProfile.firstName || ''}
                            onChange={handleUserProfileTextareas('firstName')}
                            disabled={!!userProfile.subject}
                        />
                        <TextField
                            sx={editDrawerTextFieldStyles()}
                            size="small"
                            label="Last Name"
                            variant="filled"
                            value={userProfile.lastName || ''}
                            onChange={handleUserProfileTextareas('lastName')}
                            disabled={!!userProfile.subject}
                        />
                        <TextField
                            sx={editDrawerTextFieldStyles()}
                            size="small"
                            label="Company Key"
                            variant="filled"
                            value={userProfile.companyKey ?? ''}
                            onChange={handleUserProfileTextareas('companyKey')}
                            disabled={!!userProfile.subject}
                        />
                        <ReadOnlyTextField label="Identity Provider" value={userProfile.oemIdPName || ''} />
                        {nameInConflict &&
                            <Box component='div'
                                display='inline'
                                sx={{
                                    color: 'error.main',
                                    marginTop: '6px !important',
                                    fontSize: '14px'
                                }}
                            >
                                This Identity is already connected to a User
                            </Box>
                        }
                    </>
                }
            </Stack>
        </EditDrawer>
    );
}

export function UserProfileIdentitiesDrawer() {
    const userDetails = useAppSelector(selectUserDetails);
    const currentEntity = useAppSelector(selectCurrentEntity);
    const identityDrawerProps = {
        oemId: userDetails?.userId ?? ''
    };

    const drawerState = useUserProfileIdentitiesDrawer('Identity', identityDrawerProps);

    if (currentEntity !== DrawerEntitiesNames.UserIdentities) {
        return null;
    }

    if (drawerState.action === Operations.Create || drawerState.action === Operations.Link) {
        return (
            <CreateContent
                entityName="Identity"
                processing={drawerState.processing}
                nameInConflict={drawerState.nameInConflict}
                userProfile={drawerState.userProfile}
                handleUserProfileChange={drawerState.handleUserProfileChange}
                handleUserProfileTextareas={drawerState.handleUserProfileTextareas}
                postCallback={drawerState.postCallback}
            />
        );
    }

    return <></>;
}
